import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Dither from './Dither';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

export default function Leadership() {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const t = translations[language];

    const team = [
        {
            name: "ABRAHAM MORA-TADEO",
            role: t.leadership_role_ceo,
            bio: t.leadership_bio_ceo
        },
        {
            name: "SABINA CERVANTES",
            role: t.leadership_role_coo,
            bio: t.leadership_bio_coo
        },
        {
            name: "DANIEL MORA-TADEO",
            role: t.leadership_role_hod,
            bio: t.leadership_bio_hod
        }
    ];

    return (
        <section className="w-full min-h-screen bg-background flex flex-col relative overflow-hidden z-0">
            
            {/* Massive Dither Shape Window */}
            <div className="absolute top-1/3 -right-1/4 w-[150vw] h-[150vw] md:-right-1/4 md:w-[60vw] md:h-[60vw] rounded-full overflow-hidden z-0 opacity-60 border border-white/5 shadow-[0_0_100px_rgba(56,189,248,0.2)] pointer-events-none">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.4}
                    waveFrequency={2.5}
                    waveSpeed={0.04}
                />
            </div>

            {/* Massive Title */}
            <div className="p-6 md:p-16 border-b-brut min-h-[40vh] flex flex-col justify-center relative z-10 pt-32">
                <motion.h2 
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-[clamp(4rem,10vw,14rem)] font-heading font-extrabold uppercase leading-[0.85] tracking-tighter text-foreground"
                >
                    {t.leadership_our}<br />{t.leadership_team}
                </motion.h2>
            </div>

            {/* Section Tag */}
            <div className="p-4 md:p-6 border-b-brut bg-black/40 backdrop-blur-md relative z-10">
                <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tighter">
                    {t.leadership_title}
                </h3>
            </div>

            {/* Team Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 flex-1 relative bg-black/20 backdrop-blur-sm z-10">
                {team.map((member, index) => {
                    const isAbraham = member.name === "ABRAHAM MORA-TADEO";
                    return (
                    <motion.div 
                        key={member.name}
                        onClick={() => isAbraham && navigate('/leadership/abraham')}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + (index * 0.1), duration: 0.6 }}
                        className={`p-8 md:p-12 flex flex-col gap-10 border-b-brut md:border-b-0 ${index < 2 ? 'md:border-r-brut' : ''} ${isAbraham ? 'cursor-pointer hover:bg-white/5 transition-colors group' : ''}`}
                    >
                        <h4 className="font-mono text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-accent flex justify-between items-center">
                            <span>{t.leadership_member}0{index + 1}</span>
                            {isAbraham && (
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity tracking-widest text-[10px] text-accent bg-accent/10 px-2 py-1 border border-accent/20">
                                    {t.leadership_view_profile}
                                </span>
                            )}
                        </h4>
                        
                        <div className="flex flex-col gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.leadership_name_label}</span>
                            <span className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter text-foreground leading-none">
                                {member.name}
                            </span>
                        </div>

                        <div className="flex flex-col gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.leadership_role_label}</span>
                            <span className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tighter text-accent/80 leading-none">
                                {member.role}
                            </span>
                        </div>

                        <div className="flex flex-col gap-2 mt-auto">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.leadership_about_label}</span>
                            <p className="font-mono text-sm font-bold uppercase leading-relaxed text-foreground opacity-90 tracking-wide">
                                {member.bio}
                            </p>
                        </div>
                    </motion.div>
                )})}
            </div>

            {/* Footer Tag */}
            <div className="p-6 border-t-brut bg-background/50 backdrop-blur-sm relative z-10">
                <span className="font-mono text-[10px] text-muted uppercase tracking-[0.5em]">MORATADEO_CORE_SYSTEM_V2.0</span>
            </div>

        </section>
    );
}
